<template>
  <div class="standardpage">
    <b-card>
      <b-card-header>
        <b-row>
          <b-col cols="12" sm="6" class="text-right text-sm-left pt-1">
            <i class="fa fa-upload text-primary mr-1"></i>
            <span>{{ t("page_title") }}</span>
          </b-col>
        </b-row>
      </b-card-header>

      <b-card-body class="pt-2">
        <p class="small text-muted mb-2">{{ t("page_intro") }}</p>

        <b-alert :show="!!notice" :variant="notice ? notice.variant : 'secondary'" class="py-2">
          <i class="fa fa-exclamation-circle mr-1"></i>
          <span>{{ notice ? notice.text : "" }}</span>
          <b-button v-if="notice && notice.retry" size="sm" variant="warning" class="ml-2" :disabled="busy"
            @click="commit_run">
            <i class="fa fa-refresh mr-1"></i>{{ t("retry_button") }}
          </b-button>
        </b-alert>

        <b-tabs v-model="tab" card small>
          <!-- Load a file -->
          <b-tab title="Load a file">
            <b-overlay :show="busy" spinner-type="grow" spinner-variant="primary">
              <!-- Step A: pick a flow -->
              <b-card class="mb-2">
                <b-card-header class="py-1 px-2"><strong>A. {{ t("flow_label") }}</strong></b-card-header>
                <b-card-body class="py-2 px-2">
                  <b-row>
                    <b-col cols="12" md="6">
                      <b-form-select v-model="flowname" :options="flowoptions" size="sm"
                        :disabled="!flows.length"></b-form-select>
                    </b-col>
                  </b-row>
                  <small v-if="flow" class="text-muted d-block mt-2">
                    <span v-if="flowhint">{{ flowhint }} </span>
                    <span v-for="f in flow.files" :key="f.name" class="mr-2">
                      {{ slotname(f.name) }}
                      <b-badge :variant="isrequired(f.name) ? 'primary' : 'secondary'">
                        {{ isrequired(f.name) ? t("flow_required") : t("flow_optional") }}
                      </b-badge>
                    </span>
                    <span v-if="flow.reservation">{{ t("flow_reservation") }} </span>
                    <span>{{ t("flow_cap", { kb: Math.floor((flow.max_payload_bytes || 0) / 1024) }) }}</span>
                  </small>
                </b-card-body>
              </b-card>

              <!-- Step B: the files -->
              <b-card v-if="flow && step !== 'flow'" class="mb-2">
                <b-card-header class="py-1 px-2"><strong>B. Load the files</strong></b-card-header>
                <b-card-body class="py-2 px-2">
                  <b-form-group :label="t('dateformat_label')" label-size="sm" :description="t('dateformat_hint')">
                    <b-form-radio-group v-model="dateformat" :options="flow.allowed_dateformats || []" buttons
                      button-variant="outline-secondary" size="sm"></b-form-radio-group>
                  </b-form-group>

                  <ledger-loader-slot v-for="(s, i) in slots" :key="generation + ':' + flowname + ':' + s.name" :filedef="s" :index="i"
                    :dateformat="dateformat" :workbooks="workbooks" :flowreservation="!!flow.reservation"
                    @update="slot_update" @workbook="workbook_add"></ledger-loader-slot>

                  <b-alert :show="toolarge" variant="danger" class="py-2">
                    <i class="fa fa-exclamation-circle mr-1"></i>
                    {{ t("too_large", { kb: kb(bytes), cap: kb(flow.max_payload_bytes) }) }}
                  </b-alert>
                  <div v-for="m in missing" :key="m" class="small text-muted">{{ t("required_missing", { slot: slotname(m) }) }}</div>

                  <div class="d-flex align-items-center mt-2">
                    <b-button squared size="sm" variant="primary" :disabled="!ready || busy" @click="preview_run">
                      <i class="fa fa-search mr-1"></i>{{ busy ? t("preview_busy") : t("preview_button") }}
                    </b-button>
                    <small class="ml-3" :class="toolarge ? 'text-danger' : 'text-muted'">
                      {{ t("payload_meter", { kb: kb(bytes), cap: kb(flow.max_payload_bytes) }) }}
                    </small>
                  </div>
                </b-card-body>
              </b-card>

              <!-- Step C: preview -->
              <b-card v-if="preview && step === 'preview'" class="mb-2">
                <b-card-header class="py-1 px-2"><strong>C. Check the result</strong></b-card-header>
                <b-card-body class="py-2 px-2">
                  <ledger-loader-report :report="preview" mode="preview" :flow="flow" @showbatch="batches_showfingerprint">
                  </ledger-loader-report>
                  <b-alert :show="sldatewarnings.length > 0" variant="warning" class="py-2">
                    <i class="fa fa-calendar mr-1"></i><strong>{{ t("sldate_title") }}</strong>
                    <div v-for="line in sldatelines(sldatewarnings)" :key="line">{{ line }}</div>
                    <div>{{ t("sldate_advice") }}</div>
                  </b-alert>
                  <small v-if="preview.ok && !cancommit" class="text-muted d-block mb-2">{{ t("commit_denied") }}</small>
                  <div class="d-flex">
                    <b-button squared size="sm" variant="outline-secondary" class="mr-2" :disabled="busy" @click="back_to_files">
                      <i class="fa fa-arrow-left mr-1"></i>{{ t("back_button") }}
                    </b-button>
                    <b-button v-if="cancommit" squared size="sm" variant="success" :disabled="!previewok || busy"
                      @click="commit_confirm">
                      <i class="fa fa-database mr-1"></i>{{ busy ? t("commit_busy") : t("commit_button") }}
                    </b-button>
                  </div>
                </b-card-body>
              </b-card>

              <!-- Step D: done -->
              <b-card v-if="commit && step === 'done'" class="mb-2">
                <b-card-header class="py-1 px-2"><strong>D. Loaded</strong></b-card-header>
                <b-card-body class="py-2 px-2">
                  <b-alert v-if="commit.soft" show variant="info" class="py-2">
                    <i class="fa fa-info-circle mr-1"></i>{{ commit.batchid ? t("commit_soft", { batchid: commit.batchid }) : t("commit_soft_nobatch") }}
                  </b-alert>
                  <ledger-loader-report v-else :report="commit" mode="commit" :flow="flow" @showbatch="batches_showfingerprint">
                  </ledger-loader-report>
                  <div class="d-flex">
                    <b-button squared size="sm" variant="primary" class="mr-2" @click="reset(true)">
                      <i class="fa fa-plus mr-1"></i>{{ t("again_button") }}
                    </b-button>
                    <b-button v-if="canbatches" squared size="sm" variant="outline-secondary"
                      @click="batches_showfingerprint(commit.fingerprint || (preview && preview.fingerprint))">
                      <i class="fa fa-list mr-1"></i>{{ t("viewbatch_button") }}
                    </b-button>
                  </div>
                </b-card-body>
              </b-card>

              <b-card v-if="step === 'stopped'" class="mb-2">
                <b-card-body class="py-2 px-2">
                  <b-button squared size="sm" variant="primary" @click="reset(true)">
                    <i class="fa fa-plus mr-1"></i>{{ t("again_button") }}
                  </b-button>
                </b-card-body>
              </b-card>
            </b-overlay>
          </b-tab>

          <!-- Batches -->
          <b-tab v-if="canbatches" :title="t('batches_title')">
            <p class="small text-muted mb-2">{{ t("batches_hint") }}</p>
            <b-alert :show="!!ledgerbatches.error" variant="danger" class="py-2">
              <i class="fa fa-exclamation-circle mr-1"></i>{{ ledgerbatches.error }}
            </b-alert>

            <b-card v-if="showfilter" class="w-100pct mb-2">
              <b-card-body class="py-0">
                <b-row>
                  <b-col cols="12" sm="6" md="4" xl="3">
                    <b-form-group label-cols="12" :label="t('batches_flow')" label-size="sm">
                      <b-form-select v-model="ledgerbatches.filter.flow" :options="batchflowoptions" size="sm"
                        :disabled="!!ledgerbatches.filter.fingerprint"></b-form-select>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" sm="6" md="4" xl="3">
                    <b-form-group label-cols="12" :label="t('batches_by')" label-size="sm">
                      <b-input-group>
                        <b-form-input v-model="ledgerbatches.filter.created_by" :debounce="500" trim size="sm"
                          :disabled="!!ledgerbatches.filter.fingerprint"></b-form-input>
                        <b-input-group-append>
                          <b-button size="sm" variant="outline-secondary" :disabled="!ledgerbatches.filter.created_by"
                            @click="ledgerbatches.filter.created_by = ''">
                            <i class="fa fa-remove"></i>
                          </b-button>
                        </b-input-group-append>
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

            <div v-if="ledgerbatches.filter.fingerprint" class="mb-2">
              <b-badge variant="info">{{ t("batches_fp") }}</b-badge>
              <code class="small ml-1">{{ ledgerbatches.filter.fingerprint }}</code>
              <b-button size="sm" variant="link" class="py-0" @click="ledgerbatches.filter.fingerprint = null">
                <i class="fa fa-remove"></i>
              </b-button>
            </div>

            <d-table classdivtable="availtable datatable" :refid="ledgerbatches.refid" :icon="ledgerbatches.icon"
              :tools="ledgerbatches.tools" :fields="ledgerbatches.fields" :items="ledgerbatches.items"
              :busy="ledgerbatches.busy" :offset="0" :limit="ledgerbatches.limit" :page="1" :showpages="false"
              :downloaded="ledgerbatches.downloaded" @load="batches_load" @click="ledgerbatches_click"
              @select="ledgerbatches_select">
            </d-table>
            <div class="small text-muted" v-if="!ledgerbatches.busy && !ledgerbatches.items.length">{{ t("batches_empty") }}</div>
            <b-button v-if="ledgerbatches.more" squared size="sm" variant="outline-secondary" class="mt-2"
              :disabled="ledgerbatches.busy" @click="batches_older">
              <i class="fa fa-angle-double-down mr-1"></i>{{ t("batches_older") }}
            </b-button>
          </b-tab>
        </b-tabs>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { FLOWS, PREVIEW, COMMIT, BATCHES } from "./Components/ledgerloader.gql";
import { payloadbytes, controlsvalid, sldatewarnings } from "./Components/ledgerloadercsv";
import { text, flowname, slotname, sldatelines, FLOWHINTS } from "./Components/ledgerloadercopy";
import LedgerLoaderSlot from "./Components/LedgerLoaderSlot";
import LedgerLoaderReport from "./Components/LedgerLoaderReport";

const BATCH_LIMIT = 50;
const BATCH_DOWNLOAD_LIMIT = 500;

// What the UI does for each commit-path code, mirroring the categories in the
// server schema (tp_server schema/ledgerloader.gql). Used when the throw arrives
// with its message prefix only.
const COMMIT_CATEGORY = {
  E_FINGERPRINT_MISMATCH: "REPREVIEW",
  E_PREVIEW_ERRORS_PRESENT: "REPREVIEW",
  E_NUMBERS_TAKEN: "REPREVIEW",
  E_FILE_ALREADY_LOADED: "REPREVIEW",
  E_COMMIT_FAILED: "RETRY",
  E_SEQUENCE_COLLISION: "ESCALATE",
  E_ALLOCATOR_COLLISION: "ESCALATE",
  E_NO_COUNTER_UNIQUE_INDEX: "ESCALATE",
  E_DUPLICATE_BATCH: "ALREADY_COMMITTED",
  E_COMMIT_READBACK: "UNKNOWN",
};

export default {
  name: "LedgerLoader",
  components: { LedgerLoaderSlot, LedgerLoaderReport },
  data() {
    return {
      tab: 0, // 0 Load, 1 Batches
      step: "flow", // flow | files | preview | done | stopped
      busy: false,
      notice: null, // { variant, text, retry }
      flows: [],
      flowname: null,
      dateformat: null,
      slots: [],
      generation: 0, // bumped on every flow pick or reset so slot children start fresh
      workbooks: {},
      variables: null, // the exact object sent to preview; reused for commit
      bytes: 0,
      preview: null,
      sldatewarnings: [], // [{ date, rows }] for the Sales Ledger flow, set at preview time
      commit: null,
      retried: false,
      showfilter: false,
      ledgerbatches: {
        title: "Batches",
        icon: "fa fa-list text-primary",
        refid: "ledgerbatches",
        busy: false,
        tools: "go,filter,download,clipboard",
        fields: [
          { key: "id", label: "Batch" },
          { key: "flow", label: "Flow", formatter: (v) => flowname(v) },
          {
            key: "files",
            label: "Files",
            formatter: (v) =>
              (v || []).map((f) => slotname(f.file) + " " + f.rows).join(", "),
          },
          { key: "range_start", label: "Range start", class: "text-right" },
          { key: "range_end", label: "Range end", class: "text-right" },
          { key: "sequence_set_to", label: "Counter set to", class: "text-right" },
          {
            key: "assigned",
            label: "Numbers assigned",
            formatter: (v) =>
              v
                ? text("batches_assigned", { estart: v.entry_start, eend: v.entry_end, rstart: v.ref_start, rend: v.ref_end })
                : "",
          },
          { key: "rows_inserted", label: "Rows", class: "text-right" },
          { key: "created_by", label: "Loaded by" },
          {
            key: "created_at",
            label: "Loaded at",
            formatter: (v) => {
              if (!v) return "";
              const m = this.$moment(v);
              return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : v;
            },
          },
        ],
        items: [],
        downloaded: [],
        selected: [],
        denied: [],
        limit: BATCH_LIMIT,
        before_id: null,
        more: false,
        error: null,
        loaded: false,
        filter: { flow: null, created_by: "", fingerprint: null },
      },
    };
  },
  computed: {
    flow() {
      return this.flows.find((f) => f.name === this.flowname) || null;
    },
    flowoptions() {
      return this.flows.map((f) => ({ value: f.name, text: flowname(f.name) }));
    },
    flowhint() {
      return this.flow ? FLOWHINTS[this.flow.name] || "" : "";
    },
    batchflowoptions() {
      return [{ value: null, text: text("batches_all") }].concat(this.flowoptions);
    },
    cancommit() {
      return this.ledgerloaderallowed("/api/mutation/ledgerloader_commit");
    },
    canbatches() {
      return this.ledgerloaderallowed("/api/query/ledgerloader_batches");
    },
    toolarge() {
      return !!this.flow && this.bytes > this.flow.max_payload_bytes;
    },
    missing() {
      return this.slots.filter((s) => s.required && !s.csv).map((s) => s.name);
    },
    ready() {
      if (!this.flow || this.toolarge || !this.variables) return false;
      if (!this.variables.files.length) return false;
      return this.slots.every((s) => (!s.required || s.csv) && !s.error && controlsvalid(s));
    },
    hasstopwarning() {
      return (
        !!this.preview &&
        (this.preview.issue_summary || []).some((i) => i.code === "W_NO_COUNTER_UNIQUE_INDEX")
      );
    },
    previewok() {
      return (
        !!this.preview && !!this.preview.ok && !this.preview.duplicate_of_batchid && !this.hasstopwarning
      );
    },
  },
  watch: {
    flowname() {
      this.flow_pick();
    },
    dateformat() {
      this.rebuild();
    },
    tab(v) {
      if (v === 1 && this.canbatches && !this.ledgerbatches.loaded) this.batches_reset();
    },
    "ledgerbatches.filter": {
      deep: true,
      handler() {
        if (this.canbatches) this.batches_reset();
      },
    },
  },
  async mounted() {
    if (!this.permitted()) return;
    await this.flows_load();
  },
  async activated() {
    if (!this.permitted()) return;
    if (!this.flows.length) await this.flows_load();
  },
  methods: {
    t: text,
    slotname,
    sldatelines,
    kb(bytes) {
      return Math.floor((bytes || 0) / 1024);
    },
    isrequired(name) {
      return !!this.flow && (this.flow.required || []).includes(name);
    },
    // The route is reachable by URL even when the sidebar entry is hidden, so the
    // page gates itself with the same check the sidebar uses.
    permitted() {
      if (this.ledgerloaderallowed()) return true;
      this.$router.replace("/dashboard");
      return false;
    },
    async flows_load() {
      this.busy = true;
      try {
        const response = await this.$apollo.query({ query: FLOWS });
        if (response.errors && response.errors.length) {
          this.notice = { variant: "danger", text: text("flows_failed", { text: response.errors[0].message }) };
          this.flows = [];
        } else {
          this.flows = (response.data && response.data.ledgerloader_flows) || [];
          const preferred = this.flows.find((f) => f.name === "bl_counterblocked") || this.flows[0];
          const name = preferred ? preferred.name : null;
          if (name === this.flowname) this.flow_pick();
          else this.flowname = name;
        }
      } catch (error) {
        this.notice = { variant: "danger", text: text("flows_failed", { text: this.errortext(error) }) };
      }
      this.busy = false;
    },
    flow_pick() {
      const flow = this.flow;
      this.generation += 1;
      this.slots = flow
        ? flow.files.map((f) => ({
            name: f.name,
            table: f.table,
            required: (flow.required || []).includes(f.name),
            csv: "",
            headers: [],
            rows: 0,
            source: null,
            expected_rows: null,
            totals: [
              { column: null, expected: "" },
              { column: null, expected: "" },
            ],
            error: null,
          }))
        : [];
      this.workbooks = {};
      this.notice = null;
      this.dateformat = flow ? flow.default_dateformat : null;
      this.step = flow ? "files" : "flow";
      this.rebuild();
    },
    workbook_add(filename, entry) {
      this.$set(this.workbooks, filename, entry);
    },
    slot_update(index, patch) {
      const s = this.slots[index];
      if (!s) return;
      Object.keys(patch).forEach((k) => {
        this.$set(s, k, patch[k]);
      });
      this.rebuild();
    },
    rebuild() {
      this.variables = this.buildvariables();
      this.bytes = this.variables ? payloadbytes(PREVIEW.loc.source.body, this.variables) : 0;
      this.invalidate();
    },
    // The exact variables object for preview. Commit reuses this same object (plus
    // the fingerprint) so the server sees byte-identical files and controls.
    buildvariables() {
      if (!this.flow) return null;
      const files = [];
      const controls = [];
      this.slots.forEach((s) => {
        if (!s.csv) return;
        files.push({ file: s.name, csv: s.csv });
        const c = { file: s.name };
        let has = false;
        const er = s.expected_rows === null || s.expected_rows === undefined ? "" : String(s.expected_rows).trim();
        if (er !== "" && /^\d+$/.test(er)) {
          c.expected_rows = parseInt(er, 10);
          has = true;
        }
        const totals = (s.totals || [])
          .filter((t) => t.column && String(t.expected || "").trim() !== "")
          .map((t) => ({ column: t.column, expected: String(t.expected).trim() }));
        if (totals.length) {
          c.totals = totals;
          has = true;
        }
        if (has) controls.push(c);
      });
      const v = { flow: this.flow.name, files, dateformat: this.dateformat };
      if (controls.length) v.controls = controls;
      return v;
    },
    invalidate() {
      this.preview = null;
      this.sldatewarnings = [];
      this.commit = null;
      this.retried = false;
      if (["preview", "commit", "done", "stopped"].includes(this.step)) this.step = "files";
    },
    back_to_files() {
      this.invalidate();
    },
    async preview_run() {
      if (!this.ready) return;
      this.busy = true;
      this.notice = null;
      this.preview = null;
      this.commit = null;
      try {
        const response = await this.$apollo.mutate({ mutation: PREVIEW, variables: this.variables });
        this.preview = response.data.ledgerloader_preview;
        this.sldatewarnings = this.sldate_check();
        this.step = "preview";
      } catch (error) {
        await this.handlethrow(error, "preview");
      }
      this.busy = false;
    },
    // A workbook that sets SL Date with =TODAY() keeps the date it was last saved
    // on, so warn (without blocking) when any SL Date in the headers sent is not
    // today by the browser clock. Reads the exact CSV sent to preview.
    sldate_check() {
      if (!this.flow || this.flow.name !== "sl_headers_lines" || !this.variables) return [];
      const f = this.variables.files.find((x) => x.file === "sl_item_headers");
      return f ? sldatewarnings(f.csv, this.variables.dateformat, new Date()) : [];
    },
    async commit_confirm() {
      if (!this.previewok || !this.cancommit) return;
      const filelist = (this.preview.files || [])
        .map((f) => slotname(f.file) + ": " + f.rows + " rows")
        .join(", ");
      const counter = (this.preview.files || []).find((f) => f.counter_column);
      const assigned = this.preview.assigned;
      let range = "";
      if (assigned) {
        range = text("assign_confirm", { estart: assigned.entry_start, rstart: assigned.ref_start, count: assigned.count });
      } else if (
        this.preview.provisional_range_start !== null &&
        this.preview.provisional_range_start !== undefined
      ) {
        range =
          (counter ? counter.counter_column : "BL Counter") +
          " " +
          this.preview.provisional_range_start +
          " to " +
          this.preview.provisional_range_end +
          " (estimate). ";
      }
      const body = text("commit_confirm_body", { flow: flowname(this.flow.name), filelist, range });
      const yes = await this.$bvModal.msgBoxConfirm(body, {
        title: text("commit_confirm_title"),
        size: "lg",
        buttonSize: "sm",
        okVariant: "success",
        okTitle: "Yes, load it",
        cancelTitle: "No",
        footerClass: "p-2",
        centered: true,
      });
      if (yes) await this.commit_run();
    },
    async commit_run() {
      if (!this.preview || !this.variables) return;
      this.busy = true;
      if (this.notice && this.notice.retry) this.notice = null;
      try {
        const response = await this.$apollo.mutate({
          mutation: COMMIT,
          variables: { ...this.variables, fingerprint: this.preview.fingerprint },
        });
        this.commit = response.data.ledgerloader_commit;
        this.notice = null;
        this.step = "done";
      } catch (error) {
        await this.handlethrow(error, "commit");
      }
      this.busy = false;
    },
    errortext(error) {
      return String((error && error.message) || "").replace(/^GraphQL error:\s*/, "");
    },
    // The server's formatError (tp_server src/server.js) returns only { name, message }
    // for every GraphQL error, so extensions.category never reaches the client as
    // deployed. The schema keeps the 'E_XXX: ' message prefix on purpose, so the
    // category is derived from that prefix whenever extensions are missing.
    errorcategory(error) {
      const g = ((error && error.graphQLErrors) || [])[0];
      const ext = (g && g.extensions) || {};
      const msg = this.errortext(error);
      const code = ext.code || (/^(E_[A-Z_]+):/.exec(msg) || [])[1] || null;
      const category = ext.category || (code ? COMMIT_CATEGORY[code] : null) || null;
      const batchid = ext.batchid || (/^E_DUPLICATE_BATCH: batch (\d+) /.exec(msg) || [])[1] || null;
      return { code, category, batchid, msg };
    },
    // Looks up the committed batch by fingerprint when the throw carried no batch id
    // (the in-flight duplicate branch on the server can throw without one).
    async batchid_by_fingerprint(fp) {
      if (!fp) return null;
      try {
        const response = await this.$apollo.query({ query: BATCHES, variables: { fingerprint: fp } });
        const rows = (response.data && response.data.ledgerloader_batches) || [];
        return rows.length ? rows[0].id : null;
      } catch (error) {
        return null;
      }
    },
    // Branch on the category of a commit throw. Preview throws (permission denied,
    // unknown flow, playground refusal) carry no category and land in the default
    // branch. phase is "preview" or "commit" so a dropped connection mid-commit can
    // be told apart from one during preview.
    async handlethrow(error, phase) {
      const { category, batchid, msg } = this.errorcategory(error);
      const network = error && error.networkError;
      const toolarge = !!network && network.statusCode === 413;
      switch (category) {
        case "REPREVIEW":
          this.preview = null;
          this.commit = null;
          this.step = "files";
          // preview_run clears the notice on entry, so the explanation goes on after
          // the new preview is in. A failed re-preview keeps its own danger notice.
          await this.preview_run();
          if (this.preview) this.notice = { variant: "warning", text: text("commit_repreview") };
          break;
        case "RETRY":
          if (!this.retried) {
            this.retried = true;
            this.notice = { variant: "warning", text: text("commit_retry"), retry: true };
          } else {
            this.notice = { variant: "danger", text: text("commit_retry_failed") + " " + msg };
          }
          break;
        case "ESCALATE":
          this.step = "stopped";
          this.notice = { variant: "danger", text: text("commit_escalate") + " " + msg };
          break;
        case "ALREADY_COMMITTED": {
          const fp = this.preview ? this.preview.fingerprint : null;
          const id = batchid || (await this.batchid_by_fingerprint(fp));
          this.commit = { soft: true, batchid: id, fingerprint: fp };
          this.notice = null;
          this.step = "done";
          break;
        }
        case "UNKNOWN":
          this.commit_unverified();
          break;
        default:
          if (toolarge) {
            this.notice = {
              variant: "danger",
              text: text("too_large", { kb: this.kb(this.bytes), cap: this.kb(this.flow ? this.flow.max_payload_bytes : 0) }),
            };
          } else if (network && phase === "commit") {
            // A dropped connection or timeout after the commit was sent: the batch may
            // have landed. The fingerprint lookup is the designed recovery path.
            this.commit_unverified();
          } else {
            this.notice = { variant: "danger", text: text("server_plain", { text: msg }) };
          }
      }
    },
    commit_unverified() {
      const fp = this.preview ? this.preview.fingerprint : null;
      if (this.canbatches && fp) {
        this.notice = { variant: "warning", text: text("commit_unknown") };
        this.batches_showfingerprint(fp);
      } else {
        this.notice = { variant: "warning", text: text("commit_unknown_nobatches") };
      }
    },
    reset(keepflow = true) {
      this.notice = null;
      this.workbooks = {};
      if (keepflow && this.flow) {
        this.flow_pick();
      } else {
        this.slots = [];
        this.variables = null;
        this.bytes = 0;
        this.invalidate();
        this.step = "flow";
      }
    },

    // Batches tab
    batches_reset() {
      const state = this.ledgerbatches;
      state.items = [];
      state.before_id = null;
      state.more = false;
      return this.batches_load();
    },
    batches_variables(limit) {
      const state = this.ledgerbatches;
      const v = { limit };
      if (state.filter.fingerprint) {
        v.fingerprint = state.filter.fingerprint;
        return v;
      }
      if (state.before_id) v.before_id = state.before_id;
      if (state.filter.flow) v.flow = state.filter.flow;
      if (state.filter.created_by) v.created_by = state.filter.created_by;
      return v;
    },
    // d-table emits load(offset, limit, page) from its own controls as well (page
    // size, export dialog cancel). Those are ignored: the only injected value
    // honoured is limit === 0, the download-all sentinel. The page's own calls
    // pass no limit.
    async batches_load(offset, limit) {
      const state = this.ledgerbatches;
      if (limit !== undefined && limit !== 0) return;
      const downloadall = limit === 0;
      state.error = null;
      state.busy = true;
      state.loaded = true;
      try {
        const variables = downloadall
          ? { ...this.batches_variables(BATCH_DOWNLOAD_LIMIT), before_id: undefined }
          : this.batches_variables(state.limit);
        if (variables.before_id === undefined) delete variables.before_id;
        const response = await this.$apollo.query({ query: BATCHES, variables });
        if (response.errors && response.errors.length) {
          state.error = response.errors[0].message;
          state.busy = false;
          return;
        }
        const rows = (response.data && response.data.ledgerloader_batches) || [];
        if (downloadall) {
          state.downloaded = rows;
        } else {
          state.items = state.before_id ? state.items.concat(rows) : rows;
          state.more = !state.filter.fingerprint && rows.length === state.limit;
        }
      } catch (error) {
        state.error = this.errortext(error) || "The request did not complete.";
      }
      state.busy = false;
    },
    async batches_older() {
      const state = this.ledgerbatches;
      if (!state.items.length) return;
      state.before_id = state.items[state.items.length - 1].id;
      await this.batches_load();
    },
    batches_showfingerprint(fp) {
      if (!this.canbatches || !fp) return;
      this.ledgerbatches.loaded = true;
      this.ledgerbatches.filter.fingerprint = fp;
      this.tab = 1;
    },
    async ledgerbatches_click(a) {
      const state = this.ledgerbatches;
      switch (a) {
        case "download_view":
          state.downloaded = [];
          state.downloaded = state.items;
          break;
        case "download_all":
          state.downloaded = [];
          await this.batches_load(0, 0);
          break;
        case "filter":
          this.showfilter = !this.showfilter;
          break;
        case "go":
          await this.batches_reset();
          break;
      }
    },
    ledgerbatches_select(ss, tools) {
      const state = this.ledgerbatches;
      state.selected = ss;
      if (tools) {
        ["go", "download", "filter", "clipboard"].forEach((action) => {
          const tool = tools.find((o) => o.action == action);
          if (tool) tool.disabled = false;
        });
      }
    },
  },
};
</script>

<style></style>
